import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getGroupLabel, truncateChartLabel } from './batchCompareVisualUtils';

const PERCENT_TICK = (value) => `${value}%`;

const DOT_COLORS = {
  precision: '#16a085', // teal
  recall: '#f97316', // orange
  f1: '#a855f7', // purple (more distinct from blue/orange)
  existencePrecision: '#6f42c1', // bootstrap purple, distinct from GT precision
};

function toPercent(v) {
  if (v == null || Number.isNaN(v)) return null;
  return v * 100;
}

function CircleDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={5} fill={fill} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />;
}

function TriangleDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null;
  const r = 6;
  const points = `${cx},${cy - r} ${cx - r},${cy + r} ${cx + r},${cy + r}`;
  return <polygon points={points} fill={fill} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />;
}

function DiamondDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null;
  const r = 6;
  const points = `${cx},${cy - r} ${cx - r},${cy} ${cx},${cy + r} ${cx + r},${cy}`;
  return <polygon points={points} fill={fill} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />;
}

function SquareDot({ cx, cy, fill }) {
  if (cx == null || cy == null) return null;
  const r = 5;
  return (
    <rect
      x={cx - r}
      y={cy - r}
      width={r * 2}
      height={r * 2}
      fill={fill}
      stroke="rgba(0,0,0,0.35)"
      strokeWidth={1}
    />
  );
}

function LegendMarker({ name, color }) {
  const stroke = 'rgba(0,0,0,0.35)';
  if (name === 'Precision') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
        <circle cx="7" cy="7" r="5" fill={color} stroke={stroke} strokeWidth="1" />
      </svg>
    );
  }
  if (name === 'Recall') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
        <polygon points="7,1 1,13 13,13" fill={color} stroke={stroke} strokeWidth="1" />
      </svg>
    );
  }
  if (name === 'F1') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
        <polygon points="7,1 1,7 7,13 13,7" fill={color} stroke={stroke} strokeWidth="1" />
      </svg>
    );
  }
  if (name === 'Existence Precision') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
        <rect x="2" y="2" width="10" height="10" fill={color} stroke={stroke} strokeWidth="1" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="10" height="10" fill={color} stroke={stroke} strokeWidth="1" />
    </svg>
  );
}

function DotPlotLegend({ payload }) {
  if (!payload?.length) return null;
  return (
    <div className="w-100 d-flex justify-content-center">
      <ul className="list-unstyled d-flex flex-wrap justify-content-center gap-3 mb-0 small">
        {payload.map((entry) => (
          <li key={entry.dataKey ?? entry.value} className="d-flex align-items-center gap-2">
            <LegendMarker name={entry.value} color={entry.color} />
            <span>{entry.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function sortByF1NzAvgDesc(groups) {
  return [...(groups || [])].sort((a, b) => {
    const av = a?.stats?.f1_score?.nz_avg;
    const bv = b?.stats?.f1_score?.nz_avg;
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return bv - av;
  });
}

function DotTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded shadow-sm p-2 small">
      <div className="fw-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {p.value != null ? `${Number(p.value).toFixed(1)}%` : '—'}
        </div>
      ))}
    </div>
  );
}

function BatchCompareMetricsDotPlot({
  groups = [],
  groupKey,
  title = 'Top systems: precision, recall, F1, and existence precision (NZ avg)',
  topN = 25,
}) {
  const data = useMemo(() => {
    return sortByF1NzAvgDesc(groups)
      .slice(0, topN)
      .map((g) => {
        const label = getGroupLabel(g, groupKey);
        return {
          label,
          labelShort: truncateChartLabel(label, 34),
          Precision: toPercent(g.stats?.precision?.nz_avg),
          Recall: toPercent(g.stats?.recall?.nz_avg),
          F1: toPercent(g.stats?.f1_score?.nz_avg),
          'Existence Precision': toPercent(g.stats?.existence_precision?.nz_avg),
        };
      });
  }, [groups, groupKey, topN]);

  if (!data.length) return null;

  const height = Math.max(360, data.length * 26 + 120);
  const yAxisWidth = groupKey === 'system_key' ? 200 : 140;

  return (
    <div className="border rounded p-3 bg-white mb-4">
      <h6 className="text-muted mb-1">{title}</h6>
      <p className="small text-muted mb-3">
        Systems are ranked by F1 (NZ avg). Points show each metric’s non-zero average score.
      </p>
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 24, bottom: 16, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} tickFormatter={PERCENT_TICK} />
          <YAxis
            type="category"
            dataKey="labelShort"
            width={yAxisWidth}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<DotTooltip />} />
          <Legend content={<DotPlotLegend />} align="center"  />
          <Scatter
            name="Precision"
            dataKey="Precision"
            fill={DOT_COLORS.precision}
            shape={<CircleDot />}
            isAnimationActive={false}
          />
          <Scatter
            name="Recall"
            dataKey="Recall"
            fill={DOT_COLORS.recall}
            shape={<TriangleDot />}
            isAnimationActive={false}
          />
          <Scatter
            name="F1"
            dataKey="F1"
            fill={DOT_COLORS.f1}
            shape={<DiamondDot />}
            isAnimationActive={false}
          />
          <Scatter
            name="Existence Precision"
            dataKey="Existence Precision"
            fill={DOT_COLORS.existencePrecision}
            shape={SquareDot}
            isAnimationActive={false}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

export default BatchCompareMetricsDotPlot;

